import { useContext, useEffect, useState, useRef } from "react";
import { Button, Col, Collapse, Form, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../Auth/AuthProvider";
import { ActiveRosterContext } from "../../Contexts/ActiveRosterProvider";
import Resident from "../../Residents/Models/Resident";
import { abpnClient } from "../../Services/AbpnClient";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { TrainingProgram, TrainingProgramTypes } from "../../Shared/Models/TrainingProgram";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { EligibilityRequirements, KnownCertificationType, KnownTrainingRequirementType } from "../Models/EligibilityRequirements";
import { PostGraduateYear } from "../Models/PostGraduateYear";
import { TrainingAttestation } from "../Models/TrainingAttestation";

interface IProps {
    userTrainingProgram: TrainingProgram;
    resident: Resident;
    saveResident: (attestations: Array<TrainingAttestation>) => void;
}

export default function TrainingAttestationGrid(props: IProps) {
    const userProgramIsCAP = props.userTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.Cap;
    const [resident, setResident] = useState<Resident>(props.resident);
    const [attestation, setAttestation] = useState<TrainingAttestation | null>(null);

    const [isConfirmingAttestation, setIsConfirmingAttestation] = useState<boolean>(false);
    const [showAttestationSection, setShowAttestationSection] = useState<boolean>(false);
    const [isFastTrackAttestationWithBothRemaining, setIsFastTrackAttestationWithBothRemaining] = useState<boolean>(false);
    const [isPsychFastTrackAttestation, setIsPsychFastTrackAttestation] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [attestationCertDisplayName, setAttestationCertDisplayName] = useState<string>('');
    const [residencyOrFellowshipDisplayName, setResidencyOrFellowshipDisplayName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [attestationErrorMessage, setAttestationErrorMessage] = useState<string>("");

    const form = useRef(null);
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const { reloadRoster } = useContext(ActiveRosterContext);

    useEffect(() => {
        setIsConfirmingAttestation(false);
        setShowAttestationSection(false);
        setIsSaving(false);
        setResident(props.resident);
    }, [props.resident]);

    useEffect(() => {
        const resetGrid = async () => {
            await loadAttestationSection();
        }        

        const loadAttestationSection = async () => {
            setLoading(true);
            let capRequirementResults : EligibilityRequirements | null = null;
            let psychRequirementResults : EligibilityRequirements | null = null;
            let isCAPFastTrackEligibleForFinalAttestation = null;
            let isPsychFastTrackEligibleForFinalAttestation = null;
            let errorMessage = ""; 
            let eligibilityRequirements = await getCertificationRequirementResults();
            let attestationRequirementResults:EligibilityRequirements = new EligibilityRequirements;
            let existingPsychAttestation = props.resident.trainingAttestations.find(_ => _.isAttestedComplete && _.certifications.some(_ => _.certificationId === KnownCertificationType.Psychiatry));
            let existingCapAttestation = props.resident.trainingAttestations.find(_ => _.isAttestedComplete && _.certifications.some(_ => _.certificationId === KnownCertificationType.CAP));
            
            if (!userProgramIsCAP){
                attestationRequirementResults = eligibilityRequirements[0];
            }
            if (userProgramIsCAP){
                if(props.resident.isOnFastTrack)
                {
                    // In a fast track scenario It can have both Psych and CAP eligibility requirements.
                    // evaluate both and determine whether we are attesting to CAP first or Psych first.
                    capRequirementResults = eligibilityRequirements.find(_ => _.certificationIds.find(id => id === KnownCertificationType.CAP));
                    psychRequirementResults = eligibilityRequirements.find(_ => _.certificationIds.find(id => id === KnownCertificationType.Psychiatry));
                    
                    // is CAP eligible for final attestation
                    isCAPFastTrackEligibleForFinalAttestation = capRequirementResults.isTrainingEligibleForFinalAttestation == true;
                    
                    // is psych eligible for final attestation
                    isPsychFastTrackEligibleForFinalAttestation = psychRequirementResults.isTrainingEligibleForFinalAttestation == true;
                    
                    // determine if requirement results are CAP or Psych
                    if ((props.resident.trainingAttestations.find(_ => _.isAttestedComplete && _.certifications.some(_ => _.certificationId === KnownCertificationType.Psychiatry)))
                        && (isCAPFastTrackEligibleForFinalAttestation)) 
                    {
                        attestationRequirementResults = capRequirementResults;
                        setIsPsychFastTrackAttestation(false);
                    }
                    else if ((props.resident.trainingAttestations.find(_ => _.isAttestedComplete && _.certifications.some(_ => _.certificationId === KnownCertificationType.CAP)))
                        && (isPsychFastTrackEligibleForFinalAttestation)) 
                    {
                        attestationRequirementResults = psychRequirementResults;
                        setIsPsychFastTrackAttestation(true);
                    } 
                    else{
                        setIsFastTrackAttestationWithBothRemaining(true);
                        const getFinalProgramYearEndDate = (programTypeId: number): number | null => {
                            if (!props.resident?.postGraduateYears?.length) return null;
                            const pgys = props.resident.postGraduateYears
                                .filter(_ => _.ownedByTrainingProgramTypeId === programTypeId)
                                .sort((a, b) => Date.parse(a.endDate) - Date.parse(b.endDate));
                            if (!pgys.length) return null;
                            const latestEndDate = pgys[pgys.length - 1]?.endDate;
                            return latestEndDate ? Date.parse(latestEndDate) : null;
                        };

                        const capFinalPgyEndDate = getFinalProgramYearEndDate(TrainingProgramTypes.Cap);
                        const psychFinalPgyEndDate = getFinalProgramYearEndDate(TrainingProgramTypes.Psychiatry);

                        if (capFinalPgyEndDate !== null && psychFinalPgyEndDate !== null) {
                            if (capFinalPgyEndDate < psychFinalPgyEndDate) {
                                //Attest to CAP training if eligible, otherwise attest to psych training if eligible, otherwise default to CAP training requirements
                                if(capRequirementResults.isTrainingEligibleForFinalAttestation) {
                                    if(!existingCapAttestation) {
                                        attestationRequirementResults = capRequirementResults;
                                        setIsPsychFastTrackAttestation(false);
                                    }
                                    else {
                                        attestationRequirementResults = psychRequirementResults;
                                        setIsPsychFastTrackAttestation(true);
                                    }
                                }
                                else if(psychRequirementResults.isTrainingEligibleForFinalAttestation) {
                                    attestationRequirementResults = psychRequirementResults;
                                    setIsPsychFastTrackAttestation(true);
                                }
                                else {
                                    attestationRequirementResults = capRequirementResults ?? eligibilityRequirements[0];
                                    setIsPsychFastTrackAttestation(false);
                                }
                            }
                            else if (psychFinalPgyEndDate < capFinalPgyEndDate) {
                                //Attest to psych training if eligible, otherwise attest to CAP training if eligible, otherwise default to psych training requirements
                                if(psychRequirementResults.isTrainingEligibleForFinalAttestation) {
                                    if(!existingPsychAttestation) {
                                        attestationRequirementResults = psychRequirementResults;
                                        setIsPsychFastTrackAttestation(true);
                                    }
                                    else {
                                        attestationRequirementResults = capRequirementResults;
                                        setIsPsychFastTrackAttestation(false);
                                    }
                                }
                                else if(capRequirementResults.isTrainingEligibleForFinalAttestation) {
                                    attestationRequirementResults = capRequirementResults;
                                    setIsPsychFastTrackAttestation(false);
                                }
                                else {
                                    attestationRequirementResults = psychRequirementResults ?? eligibilityRequirements[0];
                                    setIsPsychFastTrackAttestation(true);
                                }
                            }
                            else {
                                attestationRequirementResults = eligibilityRequirements[0];
                            }
                        }
                        else if (capFinalPgyEndDate !== null) {
                            attestationRequirementResults = capRequirementResults ?? eligibilityRequirements[0];
                        }
                        else if (psychFinalPgyEndDate !== null) {
                            attestationRequirementResults = psychRequirementResults ?? eligibilityRequirements[0];
                        }
                        else {
                            attestationRequirementResults = eligibilityRequirements[0];
                        }
                    }             
                }
                else 
                    attestationRequirementResults = eligibilityRequirements[0];        
            }

            // determine text based on CAP certname
            let displayAttestationCertName = attestationRequirementResults.certificationNames.join(" and ");
            let displayFellowshipOrResidency = "";
            if (attestationRequirementResults.certificationIds.some(_=>_=== KnownCertificationType.CAP))
            {
                displayFellowshipOrResidency = " fellowship ";
            }
            else 
            {
                displayFellowshipOrResidency = " residency ";
            }

            setAttestationCertDisplayName(displayAttestationCertName);
            setResidencyOrFellowshipDisplayName(displayFellowshipOrResidency);
            
            let isPsychAlreadyAttestedByAnotherProgram = isAlreadyAttestedByAnotherProgram(existingPsychAttestation);
            let psychEligibilityRequirements = eligibilityRequirements.find(_ => _.certificationIds.includes(KnownCertificationType.Psychiatry));
            let hasEligiblePsychTrainingAttestationSequence = psychEligibilityRequirements ? await isTrainingSequenceEligibleForPsychAttestation() : false;
            let hasFailingPsychRequirement = psychEligibilityRequirements?.certificationTrainingRequirements.filter(_ => _.isRequired && _.isComplete === false && (
                _.requirementType === KnownTrainingRequirementType.ClinicalSkillEvaluation ||
                _.requirementType === KnownTrainingRequirementType.PostGraduateYear));

            if (isUserPsychProgram() && isPsychAlreadyAttestedByAnotherProgram) errorMessage = "Already Attested";

            if(userProgramIsCAP && existingCapAttestation) {
                if(!props.resident.isOnFastTrack || existingPsychAttestation)
                    errorMessage = "Already Attested";
                else if(props.resident.isOnFastTrack && !existingPsychAttestation && (!hasEligiblePsychTrainingAttestationSequence || hasFailingPsychRequirement?.length > 0))
                    errorMessage = "Training is not complete!";
            }
            
            if (!userProgramIsCAP || !props.resident.isOnFastTrack || existingPsychAttestation || !hasEligiblePsychTrainingAttestationSequence || hasFailingPsychRequirement?.length > 0) {
                if (!attestationRequirementResults.isTrainingEligibleForFinalAttestation) errorMessage = "Training is not complete!";
                setAttestationErrorMessage(errorMessage);
                setIsPsychFastTrackAttestation(false);
                updateAttestation();
                defaultPatientSafetyToTrue();
                setLoading(false);
                return;
            }
            
            if (!attestationRequirementResults.isTrainingEligibleForFinalAttestation) { 
                errorMessage = "Training is not complete!";
            }

            setAttestationErrorMessage(errorMessage);            
            updateAttestation();
            defaultPatientSafetyToTrue();
            setLoading(false);
        }
        
        const updateAttestation = () => {
            let existingAttestation = getProgramAttestation();
            let updatedAttestation = existingAttestation ? deepCopy(existingAttestation) : createNewAttestation();
            updatedAttestation.completedDate = updatedAttestation.completedDate ?? completedDateBasedOnPGY();
            setAttestation(updatedAttestation);
        }

        const defaultPatientSafetyToTrue = () => {
            let updatedAttestation = deepCopy(attestation);
            if (!updatedAttestation) updatedAttestation = createNewAttestation();
            updatedAttestation.isPatientSafetyCompleted = true;
            setAttestation(updatedAttestation);
        }

        const isAlreadyAttestedByAnotherProgram = (attestation: TrainingAttestation) => attestation && attestation.trainingProgramId !== props.userTrainingProgram.trainingProgramId

        resetGrid();
    }, [resident]);

    const getProgramAttestation = () => {
        let attestation = props.resident.trainingAttestations?.find(_ => _.trainingProgramId === props.userTrainingProgram.trainingProgramId);

        if (!attestation || (userProgramIsCAP && attestation.certifications?.find(_ => _.certificationId === KnownCertificationType.Psychiatry))) {
            attestation = null;
        }

        return attestation;
    }

    const isUserPsychProgram = () => {
        return props.userTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.Psychiatry;
    }

    const openAttestationSection = async (isBeginningFinalAttestation: boolean) => {
        setShowAttestationSection(isBeginningFinalAttestation);
    }

    const isTrainingSequenceEligibleForPsychAttestation = async () => {
        let hasMissingAttestedPGY = !resident.postGraduateYears?.length || resident.postGraduateYears.find(_ => _.sequenceNumber < 5 && !_.isAttestedComplete);
        let totalPsychPgys = resident.postGraduateYears.filter(_ => _.ownedByTrainingProgramTypeId  === TrainingProgramTypes.Psychiatry && _.isAttestedComplete).length;

        if (hasMissingAttestedPGY) return false;

        // adding fast track condition, where we should have 3 psych PGYs with attestation complete in any order to qualify for a psych attestation
        if(resident.isOnFastTrack)
        {
            if (totalPsychPgys === 3 || totalPsychPgys === 4)
                return true;
            else
                return false;
        }

        const programs = await getTrainingPrograms();
        let sortedAttestedPGYs = sortData(resident.postGraduateYears.filter(_ => _.isAttestedComplete));
        let latestPGY = sortedAttestedPGYs[sortedAttestedPGYs.length - 1];
        let isLatestYear4Or5 = latestPGY.sequenceNumber === 4 || latestPGY.sequenceNumber === 5;
        let latestYearTrainingProgram = programs.find(_ => _.trainingProgramId === latestPGY.trainingProgramId);
        let isLatestYearFromCAPProgram = latestYearTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.Cap;

        if (!isLatestYear4Or5 || !isLatestYearFromCAPProgram) return false;

        return true;
    }

    const getTrainingPrograms = async () => {
        return await abpnClient.get<Array<TrainingProgram>>("/api/trainingProgram/getTrainingPrograms", authContext);
    }

    const sortData = (data: Array<PostGraduateYear>) => {
        return data?.sort((a, b) => (new Date(a.endDate)).getTime() - (new Date(b.endDate)).getTime());
    }

    const getCertificationRequirementResults = async () => {
        let url = `/api/training/GetEligibilityRequirements?physicianId=${resident.physicianId}&programTypeId=${props.userTrainingProgram.trainingProgramTypeId}`
        return await abpnClient.get<Array<EligibilityRequirements>>(url, authContext);
    }

    const updatePatientSafetyAttestation = (e: React.ChangeEvent<HTMLInputElement>) => {
        let updatedAttestation = deepCopy(attestation);
        if (!updatedAttestation) updatedAttestation = createNewAttestation();
        updatedAttestation.isPatientSafetyCompleted = e.target.checked;
        setAttestation(updatedAttestation);
    }

    const completedDateBasedOnPGY = () => {
        if (!props.resident?.postGraduateYears?.length) return null;

        let PGYs = sortData(props.resident?.postGraduateYears);

        if (isPsychFastTrackAttestation) {
            let psychPGYsMaxEndDate = PGYs.filter(_ => _.trainingProgramId !== attestation.trainingProgramId)?.slice(-1)[0];
            let firstCapPGYEndDate = PGYs.filter(_ => _.trainingProgramId === attestation.trainingProgramId)?.slice()[0];
            return Date.parse(firstCapPGYEndDate.endDate) > Date.parse(psychPGYsMaxEndDate.endDate) ? firstCapPGYEndDate.endDate : psychPGYsMaxEndDate.endDate;
        }
        else if(props.resident.isOnFastTrack && props.userTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.Psychiatry) { //Attesting to P in P, fasttrack requirements met
            let psychPGYsMaxEndDate = PGYs.filter(_ => _.ownedByTrainingProgramTypeId === TrainingProgramTypes.Psychiatry)?.slice(-1)[0];
            let firstCapPGYEndDate = PGYs.filter(_ => _.ownedByTrainingProgramTypeId === TrainingProgramTypes.Cap)?.slice(0)[0];
            return Date.parse(firstCapPGYEndDate?.endDate) > Date.parse(psychPGYsMaxEndDate?.endDate) ? firstCapPGYEndDate.endDate : psychPGYsMaxEndDate.endDate;
        }
        else if(props.userTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.Psychiatry) {  //Attesting to P in P, non-fasttrack, get final P year
            return PGYs.filter(_ => _.ownedByTrainingProgramTypeId === TrainingProgramTypes.Psychiatry)?.slice(-1)[0]?.endDate;
        }
        else if(props.userTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.Cap) { //Attesting to CAP, non-fasttrack, get final CAP year
            return PGYs.filter(_ => _.ownedByTrainingProgramTypeId === TrainingProgramTypes.Cap)?.slice(-1)[0]?.endDate;
        }

        return PGYs?.slice(-1)[0]?.endDate;
    }

    const createNewAttestation = () => {
        return new TrainingAttestation(resident.physicianId, props.userTrainingProgram.trainingProgramId, props.userTrainingProgram.trainingProgramTypeId, completedDateBasedOnPGY());
    }

    const confirmAndSaveAttestation = () => {
        let attestationToSave = attestation ?? createNewAttestation();
        attestationToSave.isAttestedComplete = true;
        attestationToSave.isPsychFastTrack = isPsychFastTrackAttestation;
        if (isPsychFastTrackAttestation) attestationToSave.trainingProgramTypeId = TrainingProgramTypes.Psychiatry;
        if(userProgramIsCAP && !isPsychFastTrackAttestation) attestationToSave.isPatientSafetyCompleted = false;
        attestationToSave.completedDate = completedDateBasedOnPGY();
        saveTrainingAttestation(attestationToSave);
    }

    const displayConfirmation = (display: boolean) => {
        setIsConfirmingAttestation(display);
        const f = form.current;
        f.classList.add('was-validated');

        f.checkValidity()
    }

    const saveTrainingAttestation = async (attestation: TrainingAttestation) => {
        setIsSaving(true);

        let url = `/api/training/saveTrainingAttestation?programTypeId=${props.userTrainingProgram.trainingProgramTypeId}`
        let updatedTraining = await abpnClient.post<Resident>(url, attestation, authContext);

        if (isFastTrackAttestationWithBothRemaining && attestation.trainingProgramTypeId === TrainingProgramTypes.Psychiatry) {
            props.saveResident(updatedTraining.trainingAttestations);
            setIsFastTrackAttestationWithBothRemaining(false);
        } else {
            navigate("/");
            reloadRoster();
        }

        setIsSaving(false);
    }

    const getFinalApproveButton = () => {
        return <Button
            className="pc-button me-2"
            variant="primary"
            size="sm"
            disabled={attestationErrorMessage !== ""}
            hidden={showAttestationSection}
            onClick={() => openAttestationSection(true)}>
            Begin Final Attestation
        </Button>;
    }

    return (

        <>
            <div className="d-flex justify-content-between">
                <h5 className="fw-bolder ml-2 mt-1 mb-4 text-primary">Final Attestation of Completed Residency Training</h5>
                <div>
                    {loading ? <AbpnSpinner /> :
                        attestationErrorMessage ?
                            <OverlayTrigger placement="right" overlay={<Tooltip id="training_not_complete" bsPrefix="pc-tooltip_warning">{attestationErrorMessage}</Tooltip>}>
                                <span className="d-inline-block">
                                    {getFinalApproveButton()}
                                </span>
                            </OverlayTrigger> : getFinalApproveButton()
                    }
                </div>
            </div>

            {showAttestationSection &&
            <Collapse in={showAttestationSection}>
                <Form noValidate ref={form}>
                    {props.resident.isOnFastTrack && <h5 className="fw-bolder ml-2 mt-1 mb-4 text-primary">{attestationCertDisplayName}</h5>}
                    {(!userProgramIsCAP || isPsychFastTrackAttestation) &&
                        <>
                            <h6 className="fw-bolder ml-2 mt-1 mb-4 text-secondary">Patient Safety
                                <OverlayTrigger placement={"right"} overlay={<Tooltip id="isAttestedABPN" bsPrefix="pc-tooltip">Optional Resident Training Attestation</Tooltip>}>
                                    <i className="las la-info-circle ml-1 ms-2 text-pc-secondary"></i>
                                </OverlayTrigger>
                            </h6>
                            <Row className="ms-2">
                                <Form.Check type="checkbox">
                                    <Form.Check.Input defaultChecked={!userProgramIsCAP || isPsychFastTrackAttestation} className="pc-checkbox me-2" type="checkbox" onChange={(e) => updatePatientSafetyAttestation(e)} />
                                    <Form.Check.Label className="ms-2">
                                        <div className="mb-2">
                                            As part of the residency training program, Dr. {resident.name.lastName} has completed a Patient Safety activity that provided adequate knowledge of safety science and principles.
                                        </div>
                                        <div>
                                            Note: ABPN Diplomates are required to complete 1 Patient Safety activity to meet continuing certification program requirements. The Patient Safety activity can be completed while in residency prior to achieving board certification. Program attestation that this requirement has been completed during residency streamlines the process for the physician and the ABPN by attestation that this continuing certification requirement has been met.
                                        </div>
                                    </Form.Check.Label>
                                </Form.Check>
                            </Row>
                        </>
                    }
                    <Row>
                        <Form.Group>
                            <Form.Label className="mb-1">
                                <div className="fw-bolder ml-2 mt-4 mb-4 text-secondary">
                                    Date of Completion
                                </div>
                                <div className="fw-normal mb-2">
                                    {new Date(completedDateBasedOnPGY()).toLocaleDateString()}
                                </div>
                            </Form.Label>
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col>
                            <h6 className="fw-bolder ml-2 mt-4 mb-4 text-secondary">Attest to the Completion of Training</h6>
                            <div className="mb-2">
                                Dr. {resident.name.lastName} has demonstrated sufficient ability to practice completely independently in the field with the level of professionalism expected of a physician in practice. 
                                There is no evidence of unethical or unprofessional behavior, or of clinical incompetence, or, if there were any concerns of the preceding at any point during training, the behaviors or concerns 
                                in question were fully and successfully remediated.
                            </div>
                            <div className="mb-2">
                                Attestation to the successful completion of residency training by the program director confirms that the resident has successfully completed all necessary training. With the attestation, Dr. {resident.name.lastName} will
                                {isPsychFastTrackAttestation ? " remain on your program's active roster to complete further training" :
                                                                " be removed from your program's active roster and added to the alumni roster"
                                }.
                            </div>
                        </Col>
                    </Row>
                    <div className="mt-3 mb-3">
                        <strong>Do you attest that {residencyOrFellowshipDisplayName} training in {attestationCertDisplayName} is complete?</strong>
                    </div>
                    <Button className="pc-button me-4" variant="primary" size="sm" disabled={attestationErrorMessage !== ""} onClick={() => displayConfirmation(true)}>Yes, Attest</Button>
                    <Button className="pc-button" variant="primary" size="sm" onClick={() => {setShowAttestationSection(false); setAttestation(null);}}>No, Cancel</Button>
                </Form>
            </Collapse>

            }
            <Modal show={isConfirmingAttestation} onHide={() => setIsConfirmingAttestation(false)}>
                <Modal.Body>
                    <div className="mb-2">
                        Attesting that a resident has successfully completed training in {attestationCertDisplayName} will
                        {isPsychFastTrackAttestation ? " leave them on your active roster as they complete further training. Their training status will remain as 'Active - Training Not Completed'" :
                            " remove them from your active roster and add them to your alumni roster. All of the resident details displayed on this page will be accessible, but uneditable, from the Alumni roster. Their Alumni Resident Details will display with a status of 'Alumni - Program completed'"
                        }.
                    </div>
                    <strong>Are you sure you want to make the final attestation that Dr. {resident.name.lastName} completed residency training in {attestationCertDisplayName}?</strong>
                </Modal.Body>
                <Modal.Footer>
                    {isSaving ? <AbpnSpinner /> :
                        <>
                            <Button variant="primary" className="me-4" disabled={attestationErrorMessage !== ""} onClick={() => confirmAndSaveAttestation()}>
                                Yes, Confirm
                            </Button>
                            <Button variant="outline-secondary" onClick={() => setIsConfirmingAttestation(false)}>
                                No, Cancel
                            </Button>
                        </>
                    }
                </Modal.Footer>
            </Modal>
        </>
    );
}